// ** React Imports
import { Fragment, useEffect, useState } from 'react';

// ** MUI Imports
import {
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import MuiCard from '@mui/material/Card';

// ** Icon Imports
import Icon from "./icon";
import CloseIcon from '@mui/icons-material/Close';
import VideoPlaceholderIcon from 'images/video_placeholder.png';
import CSV_PlaceholderIcon from 'images/Attachment_Icons/CSV__PlaceholderIcon.png';
import PDF_PlaceholderIcon from 'images/Attachment_Icons/PDF_PlaceholderIcon.png';
import DOCX_PlaceholderIcon from 'images/Attachment_Icons/DOCX__PlaceholderIcon.png';
import XLSX_PlaceholderIcon from 'images/Attachment_Icons/XLSX__PlaceholderIcon.png';
import FilePlaceholder from 'images/image_placeholder.png';


// ** Third-Party Imports
import Papa from 'papaparse'
import * as XLSX from 'xlsx'
import { ToastContainer, toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { useDropzone } from "react-dropzone";
import { DownloadAttachments } from "components/logic/DownloadAttachments";
import { dev_baseUrl } from 'utils/var';
// import ExcelCSVImport from "../../../components/ExcelCSVImport";

const renderFilePreview = (file) => {
  let icon;
  if (file.name.includes('.xlsx')) {
    icon = "../../images/xlsx.png";
  } else if (file.name.includes('.csv')) {
    icon = "../../images/csv.png";
  } else if (file.type.includes('image')) {
    icon = "../../images/image_placeholder.png"; // Placeholder for images
  } else if (file.name.includes('jpg')) {
    icon = "../../images/image_placeholder.png"; // Placeholder for images
  } else if (file.name.includes('jpeg')) {
    icon = "../../images/image_placeholder.png"; // Placeholder for images
  } else if (file.name.includes('png') || file.name.includes('PNG')) {
    icon = "../../images/image_placeholder.png"; // Placeholder for images
  } else if (file.name.includes('.pdf')) {
    icon = "../../images/pdf_placeholder.png"; // Placeholder for PDFs
  } else {
    icon = "../../images/placeholder.png"; // Optional: a default placeholder for unsupported types
  }

  return <img src={icon} height="80" alt="file preview" />;
};

const Card = styled(MuiCard)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: { width: '100%' }
}));


const AttachmentUpload = ({
  open,
  handleClose,
  Title,
  files,
  btnLoader,
  setUploadFiles,
  setFiles,
  navigate,
  setBtnLoader,
  payload,
  entity,
  setOpenUploadDlg,
  fetchAttachments,
  attachments,
}) => {
  // ** States
  const [message, setMessage] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  // const [loading, setLoading] = useState(false);

  // ** Hooks
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // const extensionToIcon = {
  //   csv: CSV_PlaceholderIcon,
  //   pdf: PDF_PlaceholderIcon,
  //   doc: DOCX_PlaceholderIcon,
  //   xlsx: XLSX_PlaceholderIcon,
  // };
  const extensionToIcon = {
    csv:  CSV_PlaceholderIcon,
    pdf:  PDF_PlaceholderIcon,
    doc:  DOCX_PlaceholderIcon,
    docx: DOCX_PlaceholderIcon,
    xls:  XLSX_PlaceholderIcon,
    xlsx: XLSX_PlaceholderIcon,
    png:  FilePlaceholder,   // ← dummy for png
    jpg:  FilePlaceholder,
    jpeg: FilePlaceholder,
    // video? still give a placeholder:
    mp4:  VideoPlaceholderIcon,
  }
  
  useEffect(() => {
    if (!files || files.length === 0) return;
    const file = files[0];
    // grab the extension
    const ext = file.name.split('.').pop().toLowerCase();
    // pick the icon (fallback to generic placeholder)
    setThumbnail(extensionToIcon[ext] || FilePlaceholder);
  }, [files]);

  // useEffect(() => {
  //   const fetchThumbnail = () => {
  //     if (files && files.length > 0) {
  //       const file = files[0];
  //       const mimeType = file.type;
  //       console.log('mimeType', mimeType)

  //       if (mimeType.startsWith('video/')) {
  //         setThumbnail(VideoPlaceholderIcon);
  //       }else if(mimeType.startsWith('image/')){
  //         const imgUrl = URL.createObjectURL(file);
  //         setThumbnail(imgUrl);
  //       }else if(mimeType.startsWith('text/')){
  //         setThumbnail(CSV_PlaceholderIcon)
  //       }else if(mimeType.startsWith('application/pdf')){
  //         setThumbnail(PDF_PlaceholderIcon)
  //       }else if(mimeType.startsWith('application/msword')){
  //         setThumbnail(DOCX_PlaceholderIcon)
  //       }else if(mimeType.startsWith('application/vnd')){
  //         setThumbnail(XLSX_PlaceholderIcon)
  //       }else{
  //         setThumbnail(FilePlaceholder);
  //       }
  //     }
  //   };

  //   if (files && files.length > 0){
  //     fetchThumbnail();
  //   }else{
  //     return;
  //   }
  // }, [files]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'video/mp4': ['.mp4'],
    },
    onDrop: acceptedFiles => {
      setBtnLoader(true);
      setTimeout(() => {
        setUploadFiles(acceptedFiles.map(file => Object.assign(file)));
        setBtnLoader(false);
      }, 1000);
    },
    onDropRejected: (fileRejections) => {
      setBtnLoader(false);
  
      const MAX_VIDEO_SIZE = 100 * 1024 * 1024; 
      const MAX_IMAGE_SIZE = 6 * 1024 * 1024;
      const MAX_OTHER_SIZE = 2 * 1024 * 1024;

      const tooManyFilesError = fileRejections.some(rejection => 
        rejection.errors.some(error => error.code === 'too-many-files')
      );
    
      if (tooManyFilesError) {
        toast.error("You can only upload 1 file at a time.", {
          duration: 3000,
          position: 'top-right',
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        return; // Exit early to avoid multiple toasts
      }
  
      const errorMessages = fileRejections.map(rejection => {
        const errors = rejection.errors.map(error => {
          let customMessage = '';
          if (error.code === 'file-too-large') {
            const fileType = rejection.file.type;
            const fileName = rejection.file.name;
            const fileSizeMB = (rejection.file.size / (1024 * 1024)).toFixed(2);
  
            // Determine size limit based on file type
            if (fileType.startsWith('video/')) {
              customMessage = `File "${fileName}" is ${fileSizeMB} MB, which exceeds the 100 MB limit for videos.`;
            } else if (fileType.startsWith('image/')) {
              customMessage = `File "${fileName}" is ${fileSizeMB} MB, which exceeds the 6 MB limit for images.`;
            } else {
              customMessage = `File "${fileName}" is ${fileSizeMB} MB, which exceeds the 2 MB limit for files.`;
            }
          } else if (error.code === 'file-invalid-type') {
            customMessage = `File "${rejection.file.name}" is not an allowed type. Accepted types are CSV, XLSX, PDF, DOC, DOCX, JPEG, PNG, MP4.`;
          }
          return customMessage || error.message;
        });
  
        return errors.join(', ');
      });
  
      errorMessages.forEach(errorMessage => {
        toast.error(errorMessage, {
          duration: 3000,
          position: 'top-right',
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      });      
      // Optional: Set error messages to a state for further use
      // setMessage(errorMessages.join('\n'));
    },
    maxFiles: 1,
    validator: file => {
      const fileType = file.type;
      const fileSize = file.size;
  
      if (fileType.startsWith('video/') && fileSize > 100 * 1024 * 1024) {
        return {
          code: 'file-too-large',
          message: 'Video files cannot exceed 100 MB.',
        };
      } else if (fileType.startsWith('image/') && fileSize > 6 * 1024 * 1024) {
        return {
          code: 'file-too-large',
          message: 'Image files cannot exceed 6 MB.',
        };
      } else if (
        !fileType.startsWith('video/') &&
        !fileType.startsWith('image/') &&
        fileSize > 2 * 1024 * 1024
      ) {
        return {
          code: 'file-too-large',
          message: 'Files other than images or videos cannot exceed 2 MB.',
        };
      }
  
      return null;
    },
  });

const handleUpload = async () => {
  setBtnLoader(true);
  const token = localStorage.getItem('jwtToken');

  if (!token) {
    setBtnLoader(true);
    navigate('/login', { state: { message: 'Session expired, please login again.' } });
    return;
  }

  const file = files[0];
  if (!file) {
    setMessage('Please select a file to upload.');
    setBtnLoader(false);
    return;
  }

  let fileName = file.name;

  if (attachments && attachments.length > 0) {
    const generateNewFileName = (name) => {
      let count = 1;
      let baseName = name.replace(/(\(\d+\))?(\.\w+)$/, '');
      let extension = name.match(/\.\w+$/)?.[0] || '';

      const normalize = (str) => str.toLowerCase().trim();

      while (attachments.some(att => normalize(att.fileName) === normalize(name))) {
        name = `${baseName}(${count++})${extension}`;
      }
      return name;
    };

    fileName = generateNewFileName(fileName);
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('FileName', fileName);
  if (entity === 'customer') {
    formData.append('EntityType', 'Customer');
    formData.append('EntityId', payload.customerID);
  }
  if (entity === 'employee') {
    formData.append('EntityType', 'Employee');
    formData.append('EntityId', payload.employeeID);
  }
  if (entity === 'request' || entity === 'Request' ) {
    formData.append('EntityType', 'Request');
    formData.append('EntityId', payload.requestID);
  }
  if (entity === 'invoice') {
    formData.append('EntityType', 'Invoice');
    formData.append('EntityId', payload.invoiceID);
  }
  if (entity === 'Assessment') {
    formData.append('EntityType', 'Request');
    formData.append('EntityId', payload.requestID);
  }
  if (entity === 'Estimate') {
    formData.append('EntityType', 'Request');
    formData.append('EntityId', payload.requestID);
  }
  if (entity === 'WorkOrder') {
    formData.append('EntityType', 'Request');
    formData.append('EntityId', payload.requestID);
  }

  formData.append('MediaType', 'Attachment');

  try {
    const response = await fetch(`${dev_baseUrl}/Media/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();

    if (response.ok && !data.isError) {
      setOpenUploadDlg(false);
      toast.success(data.message || 'File uploaded successfully.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
      setFiles([]);
      fetchAttachments();
      if (entity === 'customer') {
        navigate(`/customers/detail/${payload.customerID}`);
      }
      if (entity === 'employee') {
        navigate(`/employees/detail/${payload.employeeID}`);
      }
    } else {
      console.error('Upload Error:', data.message);
      setMessage(data.message || 'Failed to upload the file.');
      setBtnLoader(false);
    }
  } catch (error) {
    console.error('Upload Error:', error);
    setMessage('An error occurred while uploading the file. Please try again.');
    setBtnLoader(false);
  } finally {
    setBtnLoader(false);
  }
};
  
  // const navigateToNextPage = (data) => {          
  //     navigate('/customers/add-bulk-customers', { state: { data } })   
  //   }

  // const fileList = files.map(file => (
  //     <ListItem key={file.name} sx={{display:'flex', justifyContent:'center !important',marginTop:'5px',width: 'fit-content', boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px', borderRadius:'10px'}}>
  //       <Box className='file-details' sx={{display:'flex'}} >
  //         <Box className='file-preview' sx={{display:'flex', alignItems:'center', textAlign:'center'}}>{renderFilePreview(file)}
  //             <Box sx={{display:'flex', flexDirection:'column'}}>
  //                 <Typography className='file-name'>{file.name}</Typography>    
  //                 <Typography className='file-size' variant='body2'>
  //                     {Math.round(file.size / 100) / 10 > 1000
  //                     ? `${(Math.round(file.size / 100) / 10000).toFixed(1)} mb`
  //                     : `${(Math.round(file.size / 100) / 10).toFixed(1)} kb`}
  //                 </Typography> 
  //             </Box>                     
  //         </Box>            
  //       </Box>
  //       <IconButton onClick={() => handleRemoveFile(file)}>
  //         <Icon icon='tabler:x' fontSize={20} />
  //       </IconButton>
  //     </ListItem>
  //   ));

    const fileList = files.map(file => (
      <ListItem key={file.name} sx={{p:0}}>
        <Card sx={{ padding: '10px !important' }}>
          <CardContent sx={{ padding: '0px !important' }}>
            <Box sx={{display: 'flex', gap: 2}}>
              {/* <Box>{renderFilePreview(file)}</Box> */}
              <Box>
                <img
                  width={50}
                  height={50}
                  alt={file.name}
                  src={thumbnail}
                  loading="lazy"
                  // src={thumbnail || '/images/ImageLoader.gif'}
                />
              </Box>
              <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', gap: 2}}>
                <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
                  <Box>
                    <Typography className='file-name'>{file.name}</Typography>
                    <Typography className='file-size' variant='body2'>
                      {Math.round(file.size / 100) / 10 > 1000
                      ? `${(Math.round(file.size / 100) / 10000).toFixed(1)} mb`
                      : `${(Math.round(file.size / 100) / 10).toFixed(1)} kb`}
                    </Typography> 
                  </Box>
                  <Tooltip title='Remove File'>
                    <IconButton onClick={() => handleRemoveFile(file)}>
                      <Icon icon='tabler:x' fontSize={20} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 4}}>
                  <Box sx={{width: '100%'}}>
                    <LinearProgress value={100} variant="determinate"
                      sx={{
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: 'success.main'
                        },
                        height: '10px'
                      }}
                    />
                  </Box>
                  <Box color="success.main">
                    <Icon icon="tabler:circle-check-filled" />
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </ListItem>
));

  const handleRemoveFile = (file) => {
      const filteredFiles = files?.filter(i => i.name !== file.name);
      setFiles(filteredFiles);
  };
  
  return (
      <Dialog
          fullWidth={true}
          maxWidth="sm"
          open={open}
          onClose={handleClose}
          aria-labelledby="form-dialog-title"
          PaperProps={{
              sx: {
                  maxHeight: '90vh',
                  minWidth: '40vw',
                  overflow:'hidden',
              },
          }}
      >
          <DialogTitle sx={{borderBottom: '1px solid #D9D9D9', paddingBottom: '20px !important'}}>
              <Box
                  sx={{
                  display: 'flex',
                  alignItem: 'center',
                  justifyContent: 'space-between',
                  }}
              >
                  <Typography variant="h3" fontWeight='bold'>{Title}</Typography>
                  <IconButton onClick={handleClose}>
                  <Icon icon='tabler:x' />
                  </IconButton>
              </Box>
          </DialogTitle>
          <DialogContent>
                <Grid container spacing={5} my={4}>
                    <Grid item xs={12}>
                        {message && (
                        <Box mb={2} textAlign="center">
                            <Typography variant="h6" color={message.includes('successfully') ? 'green' : 'red'}>
                            {message.split('\n').map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                            </Typography>
                        </Box>
                        )}
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant='h5'>
                              Upload Attachment for 
                              { 
                              entity === 'invoice' ? ` ${payload?.invoiceTitle}` :
                              (entity === 'request' || entity === 'Request') ? ` ${payload.requestTitle}` :
                              entity === 'Assessment' ? ` ${payload.requestTitle}` :

                              entity === 'WorkOrder' ? ` ${payload.requestTitle}` :
                              
                              entity === 'Estimate' ? ` ${payload.requestTitle}` : 
                              payload.customerType === 'Business' ? ` ${payload.companyName}` : ` ${payload.firstName} ${payload.lastName}`
                              }
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Card sx={{ cursor: 'pointer' }}>
                            <CardContent>
                                    <div {...getRootProps({ className: 'dropzone' })}>
                                        <input {...getInputProps()} />
                                        <Box sx={{ display: 'flex', textAlign: 'center', alignItems: 'center', flexDirection: 'column' }}>
                                            <Box
                                                sx={{
                                                    mb: 6,
                                                    width: 48,
                                                    height: 48,
                                                    display: 'flex',
                                                    borderRadius: 1,
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: theme => `rgba(${theme.palette.customColors.main}, 0.08)`
                                                }}
                                            >
                                                <Icon icon='tabler:upload' fontSize='1.75rem' />
                                            </Box>
                                            <Typography variant='h4' sx={{ mb: 2 , fontSize:'1.2rem'}}>
                                                Click to Browse or Drag & Drop files here
                                            </Typography>
                                            <List>
                                            <ListItem disablePadding>
                                                <ListItemIcon>
                                                <Icon icon='tabler:circle-filled' height={7} width={7} />
                                                </ListItemIcon>
                                                <ListItemText>
                                                Accepted formats: CSV, XLSX, PDF, DOC, JPEG, PNG, MP4
                                                </ListItemText>
                                            </ListItem>
                                            <ListItem disablePadding>
                                                <ListItemIcon>
                                                <Icon icon='tabler:circle-filled' height={7} width={7} />
                                                </ListItemIcon>
                                                <ListItemText>
                                                Size limit: Video 100MB, Image 6MB, other files 2MB
                                                </ListItemText>
                                            </ListItem>
                                            <ListItem disablePadding>
                                                <ListItemIcon>
                                                <Icon icon='tabler:circle-filled' height={7} width={7} />
                                                </ListItemIcon>
                                                <ListItemText>
                                                Only one (1) file can be uploaded at once
                                                </ListItemText>
                                            </ListItem>
                                            </List>
                                        </Box>
                                    </div>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12}>
                    {files.length ? (
                        <Fragment>
                            <List>{fileList}</List>
                        </Fragment>
                    ) : null}
                    </Grid>                  
                </Grid>
          </DialogContent>
          <DialogActions
          sx={{ 
                display: "flex", 
                justifyContent: "space-between", 
                padding: '20px 24px !important', 
                boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`, 
                zIndex: 1 
            }}
          className="dialog-actions-dense"
        >
          <Box>
            <Button
              variant="contained"
              color="info"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Box>
          <Box>
            <Button
                variant='contained'
                color='main'
                disabled={btnLoader}
                onClick={handleUpload}
            >
                {btnLoader ? <CircularProgress size={24} /> : 'Upload'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
  );
};

export default AttachmentUpload;
